'use client';

import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';

// Paths where workspace/subscription checks are not enforced
const EXCLUDED_PREFIXES = ['/onboarding', '/settings', '/invite', '/pricing'];

const NoWorkspaceGuard = () => {
  const context = useWorkspace();
  const router = useRouter();
  const pathname = usePathname();
  // Tracks whether we've already forced a fresh subscription refetch for the
  // current "no subscription" read. Prevents a redirect loop on stale cache:
  // right after the Stripe webhook sets the trial row, the React Query cache
  // can still hold the pre-webhook empty array. We refetch once before bouncing.
  const refetchedForSubRef = useRef(false);

  useEffect(() => {
    if (!context) return;
    const {
      activeWorkspace, isLoading, isError, hasConfirmedNoWorkspace, refetchActiveWorkspace,
    } = context;

    // Wait for data to settle
    if (isLoading) return;

    // A failed workspace request is not an answer. Redirecting on it sent users
    // with a perfectly good workspace to onboarding on a transient 5xx; the
    // WorkspaceLoadErrorBanner offers a retry instead.
    if (isError) return;

    // Skip excluded paths
    if (EXCLUDED_PREFIXES.some((prefix) => pathname?.startsWith(prefix))) return;

    // No workspace at all — send to onboarding to create one. Only when the
    // server actually said so.
    if (activeWorkspace === null) {
      if (hasConfirmedNoWorkspace) router.push('/onboarding/welcome');
      return;
    }

    // Workspace exists but no active subscription/trial — payment required
    // before accessing protected pages. active_subscriptions is only populated
    // by the backend for status IN ('active', 'trial'), so an empty array means
    // no payment has been collected yet.
    const hasSubscription = (activeWorkspace.active_subscriptions ?? []).length > 0;

    if (hasSubscription) {
      // Healthy state — reset the one-shot so a future genuine downgrade still
      // gets a fresh refetch before redirecting.
      refetchedForSubRef.current = false;
      return;
    }

    // No subscription in the cached workspace. This is frequently a STALE read
    // immediately after onboarding completes (Stripe webhook → setTrial races
    // the cached GET /workspaces/active). Refetch once and let the effect re-run
    // with fresh data before deciding to redirect — this is the loop-breaker.
    if (!refetchedForSubRef.current) {
      refetchedForSubRef.current = true;
      refetchActiveWorkspace?.();
      return;
    }

    // Refetch already happened and the subscription is still absent — this is a
    // genuine no-payment state. Send the user to onboarding to complete payment.
    router.push('/onboarding/welcome');
  }, [context, pathname, router]);

  return null;
};

export default NoWorkspaceGuard;
